import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  useMediaQuery,
  useTheme as useMuiTheme,
  Pagination,
  Stack
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Work as WorkIcon,
  Phone as PhoneIcon,
  Download as DownloadIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import { buscarCandidaturasPendentes, aprovarCandidatura, rejeitarCandidatura, CandidaturaPendente } from '../../services/vaga';
import { normalizarNome, normalizarEmail, normalizarTelefone } from '../../utils/normalization';
import { downloadCurriculo, verificarCurriculo } from '../../services/candidato';

const Aprovacoes: React.FC = () => {
  const { darkMode } = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [candidaturas, setCandidaturas] = useState<CandidaturaPendente[]>([]);
  const [loading, setLoading] = useState(true);
  const [processando, setProcessando] = useState<number | null>(null);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  
  // Estados para modal de rejeição
  const [modalRejeitar, setModalRejeitar] = useState(false);
  const [candidaturaParaRejeitar, setCandidaturaParaRejeitar] = useState<number | null>(null);
  const [observacoes, setObservacoes] = useState('');

  // Estados para controlar quais candidatos têm currículo
  const [candidatosComCurriculo, setCandidatosComCurriculo] = useState<Set<number>>(new Set());
  const [verificandoCurriculos, setVerificandoCurriculos] = useState(false);

  // Estados para paginação
  const [paginaAtual, setPaginaAtual] = useState(1);
  const itensPorPagina = 10;

  // Estados para largura das colunas (redimensionáveis)
  const [larguraColunas, setLarguraColunas] = useState({
    candidato: 200,
    contato: 250,
    vaga: 180,
    cargo: 150,
    data: 120,
    curriculo: 100,
    acoes: 200
  });

  useEffect(() => {
    carregarCandidaturas();

    // Listener para recarregar quando candidato for inativado
    const handleCandidatoInativado = (event: CustomEvent) => {
      carregarCandidaturas();
    };

    // Listener para recarregar quando candidato for ativado
    const handleCandidatoAtivado = (event: CustomEvent) => {
      carregarCandidaturas();
    };

    window.addEventListener('candidatoInativado', handleCandidatoInativado as EventListener);
    window.addEventListener('candidatoAtivado', handleCandidatoAtivado as EventListener);

    return () => {
      window.removeEventListener('candidatoInativado', handleCandidatoInativado as EventListener);
      window.removeEventListener('candidatoAtivado', handleCandidatoAtivado as EventListener);
    };
  }, []);

  const carregarCandidaturas = async () => {
    setLoading(true);
    setErro('');
    
    try {
      const resultado = await buscarCandidaturasPendentes();


      setCandidaturas(resultado.data);
      
      // Verificar currículos de todos os candidatos
      verificarCurriculosCandidatos(resultado.data);
    } catch (error: any) {
      setErro(error.message || 'Erro ao carregar candidaturas pendentes');
    } finally {
      setLoading(false);
    }
  };

  const verificarCurriculosCandidatos = async (candidaturas: CandidaturaPendente[]) => {
    setVerificandoCurriculos(true);
    const curriculos = new Set<number>();

    // Obter lista única de IDs de candidatos
    const candidatosUnicos = Array.from(new Set(candidaturas.map(c => c.idCandidato)));

    // Verificar cada candidato em paralelo
    const verificacoes = candidatosUnicos.map(async (candidatoId) => {
      try {
        const temCurriculo = await verificarCurriculo(candidatoId);
        if (temCurriculo) {
          curriculos.add(candidatoId);
        }
      } catch (error) {
        // Silenciosamente ignora erros de verificação
        console.warn(`Erro ao verificar currículo do candidato ${candidatoId}:`, error);
      }
    });

    await Promise.all(verificacoes);
    setCandidatosComCurriculo(curriculos);
    setVerificandoCurriculos(false);
  };

  const handleAprovar = async (candidaturaId: number) => {
    setProcessando(candidaturaId);
    setErro('');
    setSucesso('');

    try {
      const candidatura = candidaturas.find(c => c.id === candidaturaId);
      const resultado = await aprovarCandidatura(candidaturaId, candidatura);
      
      if (resultado) {
        setSucesso('Candidatura aprovada com sucesso!');
        setCandidaturas(prev => prev.filter(c => c.id !== candidaturaId));
        
        // Notificar sidebar sobre a mudança
        window.dispatchEvent(new CustomEvent('candidaturaProcessada'));
      } else {
        setErro('Erro ao aprovar candidatura');
      }
    } catch (error: any) {
      setErro(error.message || 'Erro ao aprovar candidatura');
    } finally {
      setProcessando(null);
    }
  };

  const handleRejeitar = async () => {
    if (!candidaturaParaRejeitar) return;

    setProcessando(candidaturaParaRejeitar);
    setErro('');
    setSucesso('');

    try {
      const candidatura = candidaturas.find(c => c.id === candidaturaParaRejeitar);
      const resultado = await rejeitarCandidatura(candidaturaParaRejeitar, observacoes, candidatura);
      
      if (resultado) {
        setSucesso('Candidatura rejeitada com sucesso!');
        setCandidaturas(prev => prev.filter(c => c.id !== candidaturaParaRejeitar));
        
        // Notificar sidebar sobre a mudança
        window.dispatchEvent(new CustomEvent('candidaturaProcessada'));
        
        // Fecha o modal
        setModalRejeitar(false);
        setCandidaturaParaRejeitar(null);
        setObservacoes('');
      } else {
        setErro('Erro ao rejeitar candidatura');
      }
    } catch (error: any) {
      setErro(error.message || 'Erro ao rejeitar candidatura');
    } finally {
      setProcessando(null);
    }
  };

  const abrirModalRejeitar = (candidaturaId: number) => {
    setCandidaturaParaRejeitar(candidaturaId);
    setModalRejeitar(true);
  };

  const fecharModalRejeitar = () => {
    setModalRejeitar(false);
    setCandidaturaParaRejeitar(null);
    setObservacoes('');
  };

  // Calcular itens paginados
  const indiceInicial = (paginaAtual - 1) * itensPorPagina;
  const indiceFinal = indiceInicial + itensPorPagina;
  const candidaturasPaginadas = candidaturas.slice(indiceInicial, indiceFinal);
  const totalPaginas = Math.ceil(candidaturas.length / itensPorPagina);

  // Handler para mudança de página
  const handleMudancaPagina = (event: React.ChangeEvent<unknown>, novaPagina: number) => {
    setPaginaAtual(novaPagina);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handler para redimensionar colunas
  const handleRedimensionarColuna = (coluna: keyof typeof larguraColunas, novaLargura: number) => {
    setLarguraColunas(prev => ({
      ...prev,
      [coluna]: Math.max(80, novaLargura) // Largura mínima de 80px
    }));
  };

  const handleBaixarCurriculo = async (candidatoId: number, nomeCandidato: string) => {
    try {
      const blob = await downloadCurriculo(candidatoId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `curriculo_${nomeCandidato.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSucesso('Currículo baixado com sucesso!');
    } catch (error) {
      setErro('Erro ao baixar currículo. O candidato pode não ter enviado o documento.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: darkMode ? '#fff' : '#333' }}>
        Aprovações de Candidaturas
      </Typography>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>
          {erro}
        </Alert>
      )}

      {sucesso && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucesso('')}>
          {sucesso}
        </Alert>
      )}

      {candidaturas.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Nenhuma candidatura pendente
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Quando houver candidatos para suas vagas, eles aparecerão aqui para aprovação.
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Informação de paginação */}
          {candidaturas.length > 0 && (
            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                <Typography variant="body2" color="text.secondary">
                  Mostrando {indiceInicial + 1} - {Math.min(indiceFinal, candidaturas.length)} de {candidaturas.length} candidaturas
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Página {paginaAtual} de {totalPaginas}
                </Typography>
              </Stack>
            </Paper>
          )}

          {/* Versão Desktop - Tabela */}
          {!isMobile && (
            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 800, tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        width: `${larguraColunas.candidato}px`, 
                        minWidth: '80px',
                        resize: 'horizontal',
                        overflow: 'hidden',
                        position: 'relative',
                        '&:hover': { cursor: 'col-resize' }
                      }}
                    >
                      <strong>Candidato</strong>
                    </TableCell>
                    <TableCell sx={{ width: `${larguraColunas.contato}px`, minWidth: '80px' }}>
                      <strong>Contato</strong>
                    </TableCell>
                    <TableCell sx={{ width: `${larguraColunas.vaga}px`, minWidth: '80px' }}>
                      <strong>Vaga</strong>
                    </TableCell>
                    <TableCell sx={{ width: `${larguraColunas.cargo}px`, minWidth: '80px' }}>
                      <strong>Cargo</strong>
                    </TableCell>
                    <TableCell sx={{ width: `${larguraColunas.data}px`, minWidth: '80px' }}>
                      <strong>Data</strong>
                    </TableCell>
                    <TableCell sx={{ width: `${larguraColunas.curriculo}px`, minWidth: '80px' }}>
                      <strong>Currículo</strong>
                    </TableCell>
                    <TableCell sx={{ width: `${larguraColunas.acoes}px`, minWidth: '80px' }}>
                      <strong>Ações</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {candidaturasPaginadas.map((candidatura) => (
                    <TableRow key={candidatura.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon color="primary" />
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {normalizarNome(candidatura.nomeCandidato)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2">
                              {normalizarEmail(candidatura.emailCandidato)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2">
                              {normalizarTelefone(candidatura.telefoneCandidato || '')}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <WorkIcon color="primary" />
                          <Typography variant="subtitle2">
                            {normalizarNome(candidatura.nomeVaga)}
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2">
                          {candidatura.cargo ? normalizarNome(candidatura.cargo) : 'N/A'}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(candidatura.dataCandidatura + 'T00:00:00').toLocaleDateString('pt-BR')}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        {candidatosComCurriculo.has(candidatura.idCandidato) ? (
                          <Tooltip title="Baixar Currículo">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleBaixarCurriculo(candidatura.idCandidato, candidatura.nomeCandidato)}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Candidato não enviou currículo">
                            <span>
                              <IconButton
                                size="small"
                                disabled
                                sx={{ color: 'text.disabled' }}
                              >
                                <DescriptionIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                      
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Tooltip title="Aprovar candidatura">
                            <span>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleAprovar(candidatura.id)}
                                disabled={processando === candidatura.id}
                                startIcon={processando === candidatura.id ? <CircularProgress size={16} /> : <ApproveIcon />}
                                sx={{
                                  background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 50%, #42a5f5 100%)',
                                  '&:hover': {
                                    background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 50%, #2196f3 100%)',
                                  },
                                  '&:disabled': {
                                    background: '#9e9e9e',
                                    color: 'white'
                                  },
                                  fontWeight: 'bold',
                                  boxShadow: '0 2px 6px rgba(25,118,210,0.4)',
                                  transition: 'all 0.3s ease',
                                }}
                              >
                                Aprovar
                              </Button>
                            </span>
                          </Tooltip>
                          
                          <Tooltip title="Rejeitar candidatura">
                            <span>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => abrirModalRejeitar(candidatura.id)}
                                disabled={processando === candidatura.id}
                                startIcon={<RejectIcon />}
                                sx={{
                                  background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 50%, #ef5350 100%)',
                                  '&:hover': {
                                    background: 'linear-gradient(135deg, #c62828 0%, #d32f2f 50%, #f44336 100%)',
                                  },
                                  '&:disabled': {
                                    background: '#9e9e9e',
                                    color: 'white'
                                  },
                                  fontWeight: 'bold',
                                  boxShadow: '0 2px 6px rgba(211,47,47,0.4)',
                                  transition: 'all 0.3s ease',
                                }}
                              >
                                Rejeitar
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Versão Mobile - Cards */}
          {isMobile && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {candidaturasPaginadas.map((candidatura) => (
                <Card key={candidatura.id} elevation={2}>
                  <CardContent>
                    {/* Header do Card */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <PersonIcon color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {normalizarNome(candidatura.nomeCandidato)}
                      </Typography>
                    </Box>
                    
                    {/* Informações do Candidato */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                          {normalizarEmail(candidatura.emailCandidato)}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {normalizarTelefone(candidatura.telefoneCandidato || '')}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WorkIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {normalizarNome(candidatura.nomeVaga)}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary">
                        <strong>Cargo:</strong> {candidatura.cargo ? normalizarNome(candidatura.cargo) : 'N/A'}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary">
                        <strong>Data:</strong> {new Date(candidatura.dataCandidatura + 'T00:00:00').toLocaleDateString('pt-BR')}
                      </Typography>

                      {/* Botão de Currículo */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        {candidatosComCurriculo.has(candidatura.idCandidato) ? (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleBaixarCurriculo(candidatura.idCandidato, candidatura.nomeCandidato)}
                            sx={{ textTransform: 'none' }}
                          >
                            Baixar Currículo
                          </Button>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <DescriptionIcon sx={{ fontSize: 18 }} />
                            Sem currículo
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                  
                  <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleAprovar(candidatura.id)}
                      disabled={processando === candidatura.id}
                      startIcon={processando === candidatura.id ? <CircularProgress size={16} /> : <ApproveIcon />}
                      sx={{ 
                        flex: 1, 
                        mr: 1,
                        background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 50%, #42a5f5 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 50%, #2196f3 100%)',
                        },
                        '&:disabled': {
                          background: '#9e9e9e',
                          color: 'white'
                        },
                        fontWeight: 'bold',
                        boxShadow: '0 2px 6px rgba(25,118,210,0.4)',
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Aprovar
                    </Button>
                    
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => abrirModalRejeitar(candidatura.id)}
                      disabled={processando === candidatura.id}
                      startIcon={<RejectIcon />}
                      sx={{ 
                        flex: 1, 
                        ml: 1,
                        background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 50%, #ef5350 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #c62828 0%, #d32f2f 50%, #f44336 100%)',
                        },
                        '&:disabled': {
                          background: '#9e9e9e',
                          color: 'white'
                        },
                        fontWeight: 'bold',
                        boxShadow: '0 2px 6px rgba(211,47,47,0.4)',
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Rejeitar
                    </Button>
                  </CardActions>
                </Card>
              ))}
            </Box>
          )}

          {/* Componente de Paginação */}
          {candidaturas.length > itensPorPagina && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Stack spacing={2} alignItems="center">
                  <Pagination 
                    count={totalPaginas} 
                    page={paginaAtual} 
                    onChange={handleMudancaPagina}
                    color="primary"
                    size={isMobile ? "medium" : "large"}
                    showFirstButton
                    showLastButton
                    siblingCount={isMobile ? 0 : 1}
                    boundaryCount={1}
                    sx={{
                      '& .MuiPaginationItem-root': {
                        fontWeight: 600,
                      },
                      '& .Mui-selected': {
                        backgroundColor: '#1976d2 !important',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#1565c0 !important',
                        },
                      },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Mostrando {indiceInicial + 1} - {Math.min(indiceFinal, candidaturas.length)} de {candidaturas.length}
                  </Typography>
                </Stack>
              </Paper>
            </Box>
          )}
        </>
      )}

      {/* Modal de Rejeição */}
      <Dialog open={modalRejeitar} onClose={fecharModalRejeitar} maxWidth="sm" fullWidth>
        <DialogTitle>Rejeitar Candidatura</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Deseja adicionar alguma observação sobre a rejeição? (Opcional)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Observações"
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            placeholder="Ex: Perfil não atende aos requisitos da vaga..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharModalRejeitar} disabled={processando !== null}>
            Cancelar
          </Button>
          <Button 
            onClick={handleRejeitar} 
            color="error" 
            variant="contained"
            disabled={processando !== null}
            startIcon={processando !== null ? <CircularProgress size={16} /> : <RejectIcon />}
          >
            Rejeitar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Aprovacoes;